import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import {
  useAddAdminProfessionalRegionMutation,
  useDeleteProfessionalRegionMutation,
  useGetUserDetailsQuery,
  useSaveAdminProfessionalRegionMutation,
} from "../../../../../store/admin/professionalUserInfo";
import { PlusCircleIcon, MinusCircleIcon } from "@heroicons/react/24/outline";
import ProfessionalRegionFields from "./ProfessionalRegionFields";
import IconButton from "../../../../iconButton";

const ProfessionalRegion = ({ isMobile }) => {
  const [saveRegion, setSaveRegion] = useState(false);
  const location = useLocation();
  const { professionalId } = location?.state ?? {};

  const [showAdditionalLocationButton, setShowAdditionalLocationButton] =
    useState(false);
  const [showRemoveLocationButton, setShowRemoveLocationButton] =
    useState(false);
  const [selectedProfessionOption, setSelectedProfessionOption] =
    useState(null);

  const { data: professionalInfo, refetch } =
    useGetUserDetailsQuery(professionalId);

  const initialLocation = [
    {
      isNew: false,
      zip_code: "",
      miles: "25",
    },
  ];

  const [regions, setRegions] = useState(initialLocation);
  const [initialRegions, setInitialRegions] = useState(initialLocation);
  const [
    addProfessionalRegion,
    { isLoading: addRegionLoader, isSuccess: addRegionSuccess },
  ] = useAddAdminProfessionalRegionMutation();
  const [
    saveProfessionalRegion,
    { isLoading: saveRegionLoader, isSuccess: saveRegionSuccess },
  ] = useSaveAdminProfessionalRegionMutation();
  const [deleteRegion, { isLoading: deleteLoading, isSuccess: deleteSuccess }] =
    useDeleteProfessionalRegionMutation();

  const professions =
    professionalInfo &&
    professionalInfo?.data?.professions?.map((data) => ({
      id: data.id,
      name: data.name,
    }));

  const handleInputChange = (event, index) => {
    const inputValue = event.target.value;
    const tempRegions = [...regions];
    tempRegions[index] = {
      ...tempRegions[index],
      zip_code: inputValue,
      miles: regions[index].miles,
      isNew: regions[index].isNew ?? false,
    };
    setRegions(tempRegions);
    setSaveRegion(false);
  };

  useEffect(() => {
    if (professionalInfo && professionalInfo?.data?.regions.length > 0) {
      setRegions(professionalInfo?.data?.regions);
      setInitialRegions(professionalInfo?.data?.regions);
      setShowAdditionalLocationButton(true);
      setShowRemoveLocationButton(false);
    }
  }, [professionalInfo]);

  useEffect(() => {
    if (addRegionSuccess && !addRegionLoader) {
      setShowRemoveLocationButton(false);
      refetch();
      toast.success("You’ve successfully added a new region.");
    }
  }, [addRegionSuccess]);

  useEffect(() => {
    if (saveRegionSuccess && !saveRegionLoader) {
      setSaveRegion(true);
      setShowAdditionalLocationButton(true);
      setShowRemoveLocationButton(false);
      refetch();
      toast.success("You’ve successfully update a region.");
    }
  }, [saveRegionSuccess]);

  useEffect(() => {
    if (deleteSuccess && !deleteLoading) {
      refetch();
      toast.success("You’ve successfully deleted a region.");
    }
  }, [deleteSuccess]);

  const handleAddLocationClick = () => {
    setShowAdditionalLocationButton(false);
    setShowRemoveLocationButton(true);
    setRegions([...regions, { zip_code: "", miles: "25", isNew: true }]);
  };

  const handleRemoveLocationClick = () => {
    setShowAdditionalLocationButton(true);
    setShowRemoveLocationButton(false);
    setRegions((prevRegions) => prevRegions.slice(0, -1));
  };

  const handleSelectedPlan = (range, index) => {
    const tempRegions = [...regions];
    tempRegions[index] = {
      ...tempRegions[index],
      zip_code: regions[index].zip_code,
      miles: range,
      isNew: regions[index].isNew ?? false,
    };
    setRegions(tempRegions);
    setSaveRegion(false);
  };

  const handleSave = (region) => {
    let updateRegion = regions.find((regn) => regn.id == region.id);
    if (updateRegion.zip_code.length < 5) {
      toast.error("Zip code must be 5 digit.");
      return;
    }

    const isDuplicate = (reg) =>
      reg.zip_code == updateRegion.zip_code &&
      (updateRegion?.id
        ? updateRegion.profession_id === reg.profession_id
        : selectedProfessionOption?.id === reg.profession_id);

    const duplicateRegion = initialRegions?.some(isDuplicate);
    const duplicateRegionLength = regions.filter(isDuplicate);

    if (duplicateRegion && duplicateRegionLength.length == (showRemoveLocationButton ? 1 : 2)) {
      toast.error(
        `There is already ${
          selectedProfessionOption?.name ?? updateRegion?.profession?.name
        } associated with this ${
          updateRegion.zip_code
        }. Note that there cannot be duplicate profession(s) in a single region.`
      );
      return;
    }

    if (updateRegion.zip_code !== "") {
      const regionParams = showRemoveLocationButton
        ? {
            distance: region.miles,
            address: updateRegion.zip_code,
            user_id: professionalInfo?.data?.id,
            profession_id: selectedProfessionOption.id,
          }
        : {
            miles: parseInt(updateRegion.miles),
            zip_code: updateRegion.zip_code,
            _method: "PUT",
          };

      showRemoveLocationButton
        ? addProfessionalRegion(regionParams)
        : saveProfessionalRegion({
            regionId: updateRegion.id,
            ...regionParams,
          });
    } else {
      toast.error("Zipcode is Required");
    }
  };

  const disableSaveButton = useMemo(() => {
    return !regions.some((region) => region.isNew);
  }, [regions]);

  const savedRegions = regions.filter((region) => region.id) ?? [];

  return (
    <div>
      {!isMobile && (
        <h4 className="text-[22px] font-bold leading-[160%] text-customGray mb-6">
          Local Regions
        </h4>
      )}
      <div className="border border-solid border-[#EBEFF3] p-5 rounded-md bg-white">
        <div className="border-b border-solid border-[#EBEFF3] pb-[14px] mb-5">
          <h6 className="text-base font-bold leading-[160%] text-customGray">
            Regions
          </h6>
        </div>
        {regions &&
          regions.map((region, index) => {
            return (
              <ProfessionalRegionFields
                key={index}
                region={region}
                regionsLength={savedRegions.length}
                handleInputChange={(e) => handleInputChange(e, index)}
                handleSelectedPlan={(e) => handleSelectedPlan(e, index)}
                saveRegion={!!(region.zip_code && !region.isNew)}
                title={index + 1}
                isNewRegion={region.isNew}
                handleDelete={deleteRegion}
                unit="mi"
                hasChanges={
                  region?.id == initialRegions[index]?.id &&
                  (region?.zip_code !== initialRegions[index]?.zip_code ||
                    region?.miles !== initialRegions[index]?.miles)
                }
                handleSave={handleSave}
                professionNames={professions}
                professionalData={professionalInfo?.data}
                selectedProfessionOption={selectedProfessionOption}
                setSelectedProfessionOption={setSelectedProfessionOption}
                loader={saveRegionLoader || deleteLoading || addRegionLoader}
              />
            );
          })}
        <div className="flex">
          {showAdditionalLocationButton && (
            <IconButton
              title="Add Additional Location"
              classes="text-sm font-black leading-[160%] text-primary flex items-center gap-[6px]"
              icon={PlusCircleIcon}
              iconClasses="w-6 h-6 text-primary"
              onClick={handleAddLocationClick}
            />
          )}
          {showRemoveLocationButton && (
            <IconButton
              title="Remove Additional Location"
              classes="text-sm font-black leading-[160%] text-red-500 flex items-center gap-[6px]"
              icon={MinusCircleIcon}
              iconClasses="w-6 h-6 text-red-500"
              onClick={handleRemoveLocationClick}
            />
          )}
        </div>
      </div>

      {/* {hasChanges && (
        <div className="flex items-center gap-5 justify-end mt-[30px]">
          <Button
            title="Cancel"
            classes="text-sm font-black leading-[160%] text-[#485868] max-w-[160px] md:max-w-[128px] w-full flex items-center justify-center bg-[#EBEFF3] h-10 rounded-[10px]"
            onClick={() => setRegions(initialRegions)}
          />
          <IconButton
            title="Save"
            icon={CheckCircleIcon}
            iconClasses="w-6 h-6 text-white"
            classes={`text-sm font-black leading-[160%] text-white max-w-[160px] md:max-w-[128px] w-full flex items-center justify-center gap-[6px] bg-primary h-10 rounded-[10px] cursor-pointer opacity-1`}
            onClick={handleSave}
          />
        </div>
      )} */}
    </div>
  );
};

export default ProfessionalRegion;
